/**
 * DealDetailsModalPremium - Modal de detalhes premium do negócio
 * Layout moderno estilo ClickUp/Linear com abas e sidebar contextual
 * 
 * Características:
 * - Header compacto (48px) com ações em ícones
 * - Sistema de abas: Resumo, Chat, Negócios, Produtos, Atividades
 * - Sidebar fixa (280px) com contato, responsável e info rápidas
 * - Cores pastéis e tipografia compacta
 * - Tamanho: 94vw x 94vh
 * - Lazy loading de abas + Query unificada para contadores
 * 
 * Versão: 1.1.0
 * - Otimização: Redução de ~30 requisições para ~3 ao abrir modal
 * - Query unificada via RPC get_deal_modal_data para contadores
 * - Lazy loading nas abas (carrega dados sob demanda)
 */
import React, { useState, useEffect } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useQueryClient } from '@tanstack/react-query';
import { ChatConversationModal } from '@/components/tenant/kanban/ChatConversationModal';
import { CustomerDetailsModalPremium } from '@/components/crm/customers/modal/CustomerDetailsModalPremium';
import { CustomerFormDialog } from '@/components/crm/customers/CustomerFormDialog';
import { WinDealCustomerModal } from '@/components/crm/deals/WinDealCustomerModal';
import { useCrmDeal, useCrmDeals, CrmDeal } from '@/hooks/useCrmDeals';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { useDealActivitiesRealtime } from '@/hooks/useDealActivitiesRealtime';
import { useCrmPhasePermissions } from '@/hooks/useCrmPhasePermissions';
import { useDealModalData } from '@/hooks/useDealModalData';
import { DealFormDialog } from '@/components/crm/deals/DealFormDialog';
import { DealModalHeader } from './DealModalHeader';
import { ServiceOrderFormDialog } from '@/components/service-orders/ServiceOrderFormDialog';
// v2.7.0: modal "Alterar fase" (padronizado com Tarefas), disponível também
// para negócios ganhos/perdidos (pós-venda/onboarding em outra workspace).
import { DealChangePhaseDialog } from './DealChangePhaseDialog';
import { DealLossReasonModal } from './DealLossReasonModal';
import { useMarkDealAsLostWithReason } from '@/hooks/useCrmLossReasons';
import { usePipelinePhaseStats } from '@/hooks/usePipelinePhaseStats';
import { DealPhaseTrail } from './DealPhaseTrail';
import { toast } from 'sonner';
import { DealModalTabs, DealModalTab } from './DealModalTabs';
import { EntityShipmentsTab } from '@/components/logistics/EntityShipmentsTab';
import { ServiceOrdersEmbeddedList } from '@/components/service-orders/ServiceOrdersEmbeddedList';
import { useServiceOrdersList } from '@/hooks/useServiceOrders';
import { useServiceOrderModuleEnabled } from '@/hooks/useServiceOrderModule';
import { useShipmentsList } from '@/hooks/useShipments';
// v2.3.0: painel de processos reaproveitado na nova aba "Fluxos de trabalho"
import { WorkflowProcessPanel } from '@/components/workflow/WorkflowProcessPanel';
// v2.4.0: total de fluxos do negócio para o contador da aba (mesma chave de cache do painel)
import { useWorkflowInstanceTimeline } from '@/hooks/useWorkflowInstanceTimeline';
import { useWorkflowModule } from '@/hooks/useWorkflowModule';
// v2.6.0: ouvinte do processo elevado ao modal (cobre todas as abas, inclusive a trava dos botões)
import { useWorkflowRecordRealtime } from '@/hooks/useWorkflowRecordRealtime';
// Barra de esteira do módulo Workflow (Fase 2)
import { DealModalSidebar } from './DealModalSidebar';
import { DealSummaryTab } from './tabs/DealSummaryTab';
import { DealChatTab } from './tabs/DealChatTab';
import { DealProductsTab } from './tabs/DealProductsTab';
import { DealActivitiesTab } from './tabs/DealActivitiesTab';
import { DealAttachmentsTab } from './tabs/DealAttachmentsTab';
// v1.7.0: aba Veículos (módulo de Veículos)
import { DealVehiclesTab } from './tabs/DealVehiclesTab';
// v1.9.0: aba de Propostas Comerciais
import { DealProposalsTab } from './tabs/DealProposalsTab';
import { useVehiclesModuleEnabled } from '@/hooks/useVehiclesModule';

import { DealCadenceTab } from './tabs/DealCadenceTab';
import { DealNextActionCard } from './components/DealNextActionCard';
import { useDealCadence } from '@/hooks/useDealCadence';
import { useDealPlaybookEligibility } from '@/hooks/useDealPlaybookEligibility';
import { useStartPlaybookForDeal } from '@/hooks/useStartPlaybookForDeal';
// v2.1.0: pré-clique da trava do Workflow no botão Ganho (`crm.deal.win`)
import { useWorkflowGuardCheck } from '@/hooks/useWorkflowGuardCheck';
import { DealContractsTab } from '@/components/contracts/DealContractsTab';
import { DealChargesTab } from '@/components/financial/DealChargesTab';
// v: subabas por tipo de tarefa (Normal | Retenção | Jurídico)
import { TasksRelatedTabs } from '@/components/tasks/related/TasksRelatedTabs';
import { useFinanceiroCobrancasPaginated } from '@/hooks/useFinanceiroCobrancasPaginated';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { TooltipProvider } from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { createLogger } from '@/lib/logger';

// Logger para o módulo
const logger = createLogger('DealDetailsModalPremium');

/**
 * Props do modal premium
 */
interface DealDetailsModalPremiumProps {
  dealId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit?: (deal: CrmDeal) => void;
  onDeleted?: () => void;
  onOpenConversation?: (conversationId: string) => void;
  /** Aba inicial ao abrir o modal (padrão: 'summary') */
  initialTab?: DealModalTab;
  /** Step da cadência a pré-selecionar quando initialTab='cadence' */
  initialStepId?: string | null;
}

/**
 * Modal premium de detalhes do negócio
 */
export function DealDetailsModalPremium({ 
  dealId, 
  open, 
  onOpenChange,
  onEdit,
  onDeleted,
  onOpenConversation,
  initialTab,
  initialStepId,
}: DealDetailsModalPremiumProps) {
  const isMobile = useIsMobile();
  const { currentTenant } = useAuth();
  // v1.6.0: Permissões granulares — Financeiro (financial_charges) e Contratos (crm_contracts)
  const { canView, canCreate, canEdit, canDelete } = usePermissions();
  const canViewCharges = canView('financial_charges');
  // Criar O.S. a partir do negócio: módulo O.S. ligado + permissão de criar
  const [createOsOpen, setCreateOsOpen] = useState(false);
  // Leitura oficial do módulo O.S. (o tenant ativo não carrega essa flag)
  const serviceOrdersModuleOn = useServiceOrderModuleEnabled();
  const canCreateServiceOrder = serviceOrdersModuleOn && canCreate('os_list');
  // Aba Ordens de serviço: módulo O.S. ligado + permissão de ver a listagem
  const canViewServiceOrders = serviceOrdersModuleOn && canView('os_list');
  const canCreateCharges = canCreate('financial_charges');
  const canEditCharges = canEdit('financial_charges');
  const canDeleteCharges = canDelete('financial_charges');
  const canViewContracts = canView('crm_contracts');
  // Logística: aba Remessas (mesma consulta alimenta contador e lista — cache compartilhado)
  const canViewShipments = canView('log_shipments');
  const canCreateContracts = canCreate('crm_contracts');
  const canEditContracts = canEdit('crm_contracts');
  const canDeleteContracts = canDelete('crm_contracts');
  // v1.9.0: permissões granulares do módulo de Propostas (crm_proposals)
  const canViewProposals = canView('crm_proposals');
  const canCreateProposals = canCreate('crm_proposals');
  const canEditProposals = canEdit('crm_proposals');
  const queryClient = useQueryClient();
  // v2.0.0: assinatura realtime da timeline elevada para o modal — mantém as
  // atividades (inclusive eventos de proposta) sincronizadas em qualquer aba.
  // v2.5.0: o mesmo canal agora também escuta a linha do negócio (crm_deals),
  // atualizando trilha de fases/header sem F5 quando muda em outra sessão.
  useDealActivitiesRealtime(open ? dealId : null);
  const [activeTab, setActiveTab] = useState<DealModalTab>(initialTab ?? 'summary');
  // v2.3.0: flag do módulo Workflow — habilita a aba "Fluxos de trabalho"
  const { isEnabled: workflowEnabled } = useWorkflowModule();
  // v2.6.0: ouvinte do processo elevado da aba de fluxos para o modal — assim
  // as abas Propostas/Contratos/Financeiro também recebem o sinal que libera
  // a trava exibida nos botões (workflow-guard-check), sem precisar de F5.
  useWorkflowRecordRealtime({
    rootEntityId: dealId,
    enabled: Boolean(open && workflowEnabled && dealId),
  });

  // v2.4.0: reaproveita o cache do painel de workflow (nenhuma requisição extra)
  // apenas para alimentar o contador da aba "Fluxos de trabalho".
  const { processos: workflowProcessos } = useWorkflowInstanceTimeline({
    rootEntityType: 'crm_deal',
    rootEntityId: dealId ?? '',
    enabled: Boolean(open && workflowEnabled && dealId),
  });
  // v1.7.0: flag do módulo de Veículos (fonte única useVehiclesModule)
  // v1.8.0: além do módulo, exige permissão de visualizar o menu de veículos
  const vehiclesModuleEnabled = useVehiclesModuleEnabled();
  const vehiclesEnabled = vehiclesModuleEnabled && canView('vehicles_list');
  // v1.6.0: contagem de anexos agregados exposta pela aba Anexos após primeira abertura (lazy)
  const [attachmentsCount, setAttachmentsCount] = useState<number | undefined>(undefined);


  // Sincroniza a aba inicial quando o modal é aberto (necessário pois modal é reutilizado)
  useEffect(() => {
    if (open) {
      setActiveTab(initialTab ?? 'summary');
    }
  }, [open, initialTab]);

  // v1.6.0: Se a aba ativa virar 'charges'/'contracts' sem permissão, retorna ao 'summary'
  useEffect(() => {
    if (activeTab === 'charges' && !canViewCharges) {
      setActiveTab('summary');
    }
    if (activeTab === 'proposals' && !canViewProposals) {
      setActiveTab('summary');
    }
    if (activeTab === 'contracts' && !canViewContracts) {
      setActiveTab('summary');
    }
    // v2.3.0: módulo Workflow desligado → volta ao Resumo
    if (activeTab === 'workflow' && !workflowEnabled) {
      setActiveTab('summary');
    }
  }, [activeTab, canViewCharges, canViewContracts, canViewProposals, workflowEnabled]);


  // v2.2.0: dados de cadência ativa para mostrar aba "Cadência" + badges
  const { nextAction, steps: cadenceSteps } = useDealCadence(dealId);
  const hasCadence = !!nextAction || cadenceSteps.length > 0;
  // Contagem total de passos pendentes (inclui pending, in_progress e overdue),
  // para o badge da aba refletir o "trabalho restante" — mesmo padrão das demais abas.
  const cadenceOverdueCount = cadenceSteps.filter(
    (s) => s.step_status !== 'completed' && s.step_status !== 'skipped'
  ).length;

  // v1.2.0: Invalida cache do Kanban ao fechar o modal
  // Garante que o card reflita alterações feitas nas abas (produtos, contratos, etc.)
  useEffect(() => {
    if (!open) {
      // Invalida colunas do Kanban que contêm o deal
      // Se temos o deal carregado, invalida somente a coluna específica (preciso)
      if (deal?.pipeline_id && deal?.phase_id) {
        queryClient.invalidateQueries({
          queryKey: ['kanban-column', deal.pipeline_id, deal.phase_id],
        });
      } else {
        // Fallback: invalida todas as colunas (seguro para qualquer contexto)
        queryClient.invalidateQueries({ queryKey: ['kanban-column'] });
      }
      // Invalida também deal-modal-data para atualizar contadores se modal reabrir
      if (dealId) {
        queryClient.invalidateQueries({ queryKey: ['deal-modal-data', dealId] });
      }
      // v1.3.0: Invalida dados CRM do Kanban de Conversas para refletir alterações
      queryClient.invalidateQueries({ queryKey: ['kanban-conversation-crm-data'] });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  // v1.5.0: confirmação para iniciar playbook na fase atual
  const [startPlaybookOpen, setStartPlaybookOpen] = useState(false);
  // v2.7.0: modal "Alterar fase" do negócio (fase/pipeline/workspace)
  const [changePhaseOpen, setChangePhaseOpen] = useState(false);

  // Elegibilidade + mutation para iniciar playbook manualmente
  const { data: playbookEligibility } = useDealPlaybookEligibility(dealId);
  const startPlaybook = useStartPlaybookForDeal();
  
  // Estado para modal de chat sobreposto
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);
  
  // Estado para modal de cliente sobreposto
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  // v1.5.0: contato aberto em modo 360º (modal de contato sobreposto ao negócio)
  const [viewContactId, setViewContactId] = useState<string | null>(null);
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  
  // Estado para modal de ganhar negócio com cadastro de cliente
  const [winDealModalOpen, setWinDealModalOpen] = useState(false);
  
  // v1.4.0: Estado para modal de cadastro de cliente (transformar contato em cliente)
  const [customerFormOpen, setCustomerFormOpen] = useState(false);

  // Estado do modal de motivo de perda (sempre exigido ao concluir como perdido)
  const [lossReasonModalOpen, setLossReasonModalOpen] = useState(false);
  // Mutation dedicada que grava loss_reason_id via RPC mark_deal_as_lost
  const markAsLostWithReason = useMarkDealAsLostWithReason();

  // Handler para abrir conversa no modal sobreposto
  const handleOpenConversation = (conversationId: string) => {
    setSelectedConversationId(conversationId);
    setIsChatModalOpen(true);
  };

  // Handler para abrir modal de cliente
  const handleViewCustomer = (customerId: string) => {
    setSelectedCustomerId(customerId);
    setIsCustomerModalOpen(true);
  };

  // Busca dados do deal
  const { data: deal, isLoading, error } = useCrmDeal(dealId);
  
  // Mutations
  const { 
    moveDealToPhase, 
    updateDeal,
    markAsWon,
    markAsLost, 
    reopenDeal,
    deleteDeal 
  } = useCrmDeals(undefined, { enabled: false }); // Apenas mutations — evita SELECT * desnecessário

  // Query unificada para contadores das abas (substitui 3 queries separadas)
  const { data: modalData } = useDealModalData(dealId);

  // Contadores para abas (via RPC unificada)
  const chatCount = modalData?.counters.conversations || 0;
  const productsCount = modalData?.counters.deal_items || 0;
  const contractsCount = modalData?.counters.contracts || 0;
  // v1.4.0: contador de tarefas vinculadas
  const tasksCount = modalData?.counters.tasks || 0;
  // Logística: contador da aba Remessas (mesma consulta da lista da aba)
  const shipmentsQuery = useShipmentsList({
    search: '', status: null, carrierId: null, overdueOnly: false,
    entityType: 'deal', entityId: dealId ?? '', page: 1, pageSize: 50,
  }, open && !!dealId && canViewShipments);
  // Contador da aba Ordens de serviço (mesma consulta oficial da listagem, filtro por negócio)
  const serviceOrdersQuery = useServiceOrdersList({
    search: '', status: null, serviceTypeId: null, providerId: null, categoryId: null, phaseId: null,
    deadlineBucket: null, dateField: 'created_at', dateFrom: null, dateTo: null,
    sortField: 'created_at', sortDirection: 'desc', page: 1, pageSize: 1, dealId: dealId ?? null,
  }, open && !!dealId && canViewServiceOrders);
  // v1.5.0: contador de veículos vinculados ao negócio
  const vehiclesCount = modalData?.counters.vehicles || 0;
  // v1.6.0: contador de propostas (exceto canceladas)
  const proposalsCount = modalData?.counters.proposals || 0;

  // v1.3.0: Contador de cobranças financeiras via RPC paginada (evita SELECT direto na tabela)
  const { totalCount: chargesCount } = useFinanceiroCobrancasPaginated(
    dealId ? { deal_id: dealId } : undefined,
    { page: 1, pageSize: 1 }
  );

  // v2.0.0: Verifica se deal pode ser marcado como ganho (tem produtos/serviços)
  const canMarkAsWon = productsCount > 0;

  /**
   * v2.1.0: consulta antecipada da trava do Workflow para "Marcar como ganho".
   * A decisão final continua no banco (gatilho `workflow_tg_guard`); aqui é só
   * feedback visual (desabilitar + tooltip) — fail-open em caso de erro na RPC.
   */
  const guardMarcarGanho = useWorkflowGuardCheck({
    actionCode: 'crm.deal.win',
    rootEntityType: 'crm_deal',
    rootEntityId: dealId,
    enabled: open && !!dealId && deal?.status === 'open',
  });

  /**
   * v2.2.0: consulta antecipada da trava do Workflow para "mudar de fase".
   * Usada pela trilha de fases (DealPhaseTrail) — feedback visual apenas.
   */
  const guardMudarFase = useWorkflowGuardCheck({
    actionCode: 'crm.deal.change_phase',
    rootEntityType: 'crm_deal',
    rootEntityId: dealId,
    enabled: open && !!dealId && deal?.status === 'open',
  });



  // v1.7.0: Permissões granulares por fase para os botões do header
  // Espelha exatamente a regra usada no DealKanbanBoard/DealCard, garantindo que
  // Editar / Ganho / Perdido / Reabrir / Excluir respeitem o toggle por workspace.
  const {
    canDoOnPhase: canDoOnDealPhase,
    isGranularEnabled: isCrmGranularEnabled,
    isAdmin: isCrmAdmin,
  } = useCrmPhasePermissions(deal?.pipeline?.workspace_id ?? null);

  const canEditDealHeader = isCrmAdmin
    ? true
    : !isCrmGranularEnabled
      ? canEdit('crm_deals')
      : canDoOnDealPhase(deal?.phase_id, 'can_edit');

  const canChangePhaseHeader = isCrmAdmin
    ? true
    : !isCrmGranularEnabled
      ? canEdit('crm_deals')
      : canDoOnDealPhase(deal?.phase_id, 'can_change_phase');

  const canReopenDealHeader = isCrmAdmin
    ? true
    : !isCrmGranularEnabled
      ? canEdit('crm_deals')
      : canDoOnDealPhase(deal?.phase_id, 'can_reopen');

  const canDeleteDealHeader = isCrmAdmin
    ? true
    : !isCrmGranularEnabled
      ? canDelete('crm_deals')
      : canDoOnDealPhase(deal?.phase_id, 'can_delete');

  /**
   * Handler para marcar deal como ganho
   * v2.0.0: Verifica se há produtos/serviços antes de prosseguir
   * Verifica se contato tem cliente vinculado antes de prosseguir
   * Se não tiver, abre modal para cadastrar dados do cliente
   */
  const handleMarkWon = () => {
    if (!deal) return;
    
    // v2.0.0: Bloqueia se não há produtos/serviços
    if (!canMarkAsWon) {
      logger.warn('Tentativa de marcar como ganho sem produtos/serviços');
      return;
    }
    
    // Verifica se contato tem cliente vinculado
    const hasCustomer = deal.contact?.customer && deal.contact.customer.length > 0;
    
    if (hasCustomer) {
      // Cliente existe - marca direto como ganho (sem modal extra)
      logger.info('Contato já tem cliente vinculado, marcando como ganho direto');
      markAsWon.mutate(deal.id);
    } else {
      // Sem cliente - abre modal para cadastrar dados obrigatórios
      logger.info('Contato sem cliente vinculado, abrindo modal de cadastro');
      setWinDealModalOpen(true);
    }
  };

  // v1.5.0: Carrega fases do pipeline para resolver fases is_lost (motivos de perda)
  const { phases: pipelinePhases } = usePipelinePhaseStats(deal?.pipeline_id ?? null);
  const lostPhases = React.useMemo(
    () => (pipelinePhases ?? []).filter((p) => p.is_lost),
    [pipelinePhases]
  );

  /**
   * Handler para marcar como perdido — SEMPRE abre modal de motivo obrigatório.
   * O modal exibe também seleção de fase quando o pipeline possui 2+ fases is_lost.
   */
  const handleMarkLostClick = () => {
    if (!deal) return;
    if (lostPhases.length === 0) {
      toast.error('Configure uma fase de perda no pipeline antes de marcar como perdido');
      return;
    }
    setLossReasonModalOpen(true);
  };

  /** Confirma escolha do motivo (e fase, se aplicável) e persiste via RPC */
  const handleLossReasonConfirm = (payload: {
    lossReasonId: string;
    targetPhaseId: string | null;
    lossReasonNote: string | null;
    conversationIdsToClose?: string[];
  }) => {
    if (!deal) return;
    markAsLostWithReason.mutate(
      {
        dealId: deal.id,
        lossReasonId: payload.lossReasonId,
        targetPhaseId: payload.targetPhaseId,
        lossReasonNote: payload.lossReasonNote,
        // v1.1.0: conversas ativas selecionadas para encerrar junto
        conversationIdsToClose: payload.conversationIdsToClose ?? [],
      },
      { onSuccess: () => setLossReasonModalOpen(false) },
    );
  };


  const handlePhaseChange = (newPhaseId: string) => {
    if (!dealId || !deal || newPhaseId === deal.phase_id) return;
    moveDealToPhase.mutate({ dealId, newPhaseId });
  };

  // =========================================================================
  // v2.2.0: Trilha de fases (estilo Bitrix) — apenas apresentação/roteamento.
  // As regras já existentes continuam sendo a fonte de verdade:
  //   - permissões granulares por fase (can_view / can_change_phase)
  //   - fallback legado (canEdit('crm_deals')) quando o toggle está desligado
  //   - trava do Workflow (workflow_guard_check) para mudança de fase
  //   - fase de perda continua exigindo o modal de motivo
  // =========================================================================

  /** Fases visíveis para o usuário na trilha */
  const trailPhases = React.useMemo(() => {
    const todas = pipelinePhases ?? [];
    if (isCrmAdmin || !isCrmGranularEnabled) return todas;
    return todas.filter((p) => canDoOnDealPhase(p.phase_id, 'can_view'));
  }, [pipelinePhases, isCrmAdmin, isCrmGranularEnabled, canDoOnDealPhase]);

  /** Verifica se o usuário pode mover o negócio PARA a fase informada */
  const canMoveToTrailPhase = React.useCallback(
    (phaseId: string) => {
      if (!deal || deal.status !== 'open') return false;
      if (moveDealToPhase.isPending) return false;
      if (guardMudarFase.bloqueado) return false;
      if (isCrmAdmin) return true;
      if (!isCrmGranularEnabled) return canEdit('crm_deals');
      // Precisa poder sair da fase atual E entrar na fase destino
      return (
        canDoOnDealPhase(deal.phase_id, 'can_change_phase') &&
        canDoOnDealPhase(phaseId, 'can_change_phase')
      );
    },
    [
      deal,
      moveDealToPhase.isPending,
      guardMudarFase.bloqueado,
      isCrmAdmin,
      isCrmGranularEnabled,
      canEdit,
      canDoOnDealPhase,
    ],
  );

  /** Motivo do bloqueio exibido no tooltip da fase */
  const trailBlockedReason = React.useCallback(
    (phaseId: string) => {
      if (!deal) return null;
      if (deal.status !== 'open') return 'Negócio fechado: reabra para alterar a fase.';
      if (guardMudarFase.bloqueado) {
        return guardMudarFase.mensagem || 'Existe uma pendência do processo que impede mudar de fase.';
      }
      if (moveDealToPhase.isPending) return 'Movimentação em andamento...';
      if (phaseId === deal.phase_id) return 'Fase atual do negócio.';
      return 'Você não tem permissão para mover o negócio para esta fase.';
    },
    [deal, guardMudarFase.bloqueado, guardMudarFase.mensagem, moveDealToPhase.isPending],
  );

  /**
   * Clique na trilha: fases de perda abrem o modal de motivo (regra existente),
   * fases de ganho seguem o fluxo de "marcar como ganho" e as demais movem direto.
   */
  const handleTrailPhaseSelect = (phaseId: string) => {
    const fase = (pipelinePhases ?? []).find((p) => p.phase_id === phaseId);
    if (!fase) return;
    if (fase.is_lost) {
      handleMarkLostClick();
      return;
    }
    if (fase.is_won) {
      handleMarkWon();
      return;
    }
    handlePhaseChange(phaseId);
  };


  /**
   * Handler para salvar informações do deal via modal de edição
   *
   * CHANGELOG:
   *   v2.1.0 (2026-07-11) - Serializa as mutations `moveDealToPhase` e
   *     `updateDeal` para eliminar `deadlock detected` que ocorria quando
   *     as duas RPCs rodavam em paralelo via Promise.all no mesmo
   *     `crm_deals.id`. Ambas disparam triggers pesados (auditoria de
   *     workspace move + cadência) que adquirem locks em
   *     `crm_deal_cadences` / `crm_deal_cadence_steps` em ordens
   *     diferentes → deadlock clássico. Executando sequencialmente
   *     (primeiro move, depois patch) a contenção some. Adicionalmente,
   *     cada mutation só é disparada quando o campo correspondente
   *     realmente mudou, evitando UPDATEs redundantes.
   *   v2.0.1 - Retorna Promise para o caller aguardar a conclusão.
   *   v2.0.0 - Usa updateDeal.mutate para atualizar pipeline_id,
   *     phase_id, expected_close_date e source.
   */
  const handleInfoSave = async (data: {
    pipeline_id: string;
    phase_id: string;
    expected_close_date: string | null;
    source: string | null;
  }): Promise<void> => {
    if (!deal) return;

    // Detecta o que realmente mudou (evita UPDATEs redundantes e serializa)
    const pipelineOrPhaseChanged =
      data.pipeline_id !== deal.pipeline_id || data.phase_id !== deal.phase_id;

    const dateOrSourceChanged =
      data.expected_close_date !== (deal.expected_close_date ?? null) ||
      (data.source ?? null) !== (deal.source ?? null);

    // PASSO 1: Move pipeline/fase primeiro (dispara triggers de workspace + cadência).
    // Se falhar, o passo 2 nem roda — evita estado parcial.
    if (pipelineOrPhaseChanged) {
      await moveDealToPhase.mutateAsync({
        dealId: deal.id,
        newPhaseId: data.phase_id,
      });
    }

    // PASSO 2: Patch leve de campos adicionais (só se mudaram).
    // Executa DEPOIS do move, sem concorrência de locks no mesmo deal.
    if (dateOrSourceChanged) {
      await updateDeal.mutateAsync({
        id: deal.id,
        expected_close_date: data.expected_close_date,
        source: data.source,
      });
    }
  };

  /**
   * Salva Responsável/Vendedor a partir do lápis da seção Pessoas (sidebar).
   * Respeita a mesma permissão do botão Editar do header.
   */
  const handlePeopleSave = async (data: {
    assigned_to: string | null;
    seller_id: string | null;
  }) => {
    if (!deal) return;
    await updateDeal.mutateAsync({
      id: deal.id,
      assigned_to: data.assigned_to,
      seller_id: data.seller_id,
    });
  };

  /**
   * Deleta deal e fecha modal
   */
  const handleDelete = async () => {
    if (!dealId) return;
    await deleteDeal.mutateAsync(dealId);
    setDeleteOpen(false);
    onOpenChange(false);
    onDeleted?.();
  };

  /**
   * Abre edição via callback externo ou form interno
   */
  const handleEdit = () => {
    if (deal && onEdit) {
      onOpenChange(false);
      onEdit(deal);
    } else {
      setFormOpen(true);
    }
  };

  /**
   * Renderiza conteúdo da aba ativa
   */
  const renderTabContent = () => {
    if (!deal) return null;

    switch (activeTab) {
      case 'summary':
        return (
          <div className="space-y-3">
            {/* v2.2.0: card destacado da próxima ação da cadência */}
            <DealNextActionCard dealId={deal.id} />
            <DealSummaryTab 
              deal={deal} 
              onPhaseChange={handlePhaseChange}
              sidebarContent={isMobile ? (
                <DealModalSidebar
                  deal={deal}
                  canMarkAsWon={canMarkAsWon}
                  onViewContact={(contactId) => {
                    // v1.5.0: abre o modal 360º do contato
                    setViewContactId(contactId);
                  }}
                  onViewCustomer={handleViewCustomer}
                  onCreateCustomer={() => setCustomerFormOpen(true)}
                  onInfoSave={handleInfoSave}
                  isInfoSaving={updateDeal.isPending || moveDealToPhase.isPending}
                  canEditPeople={canEditDealHeader}
                  onPeopleSave={handlePeopleSave}
                  isPeopleSaving={updateDeal.isPending}
                  onMarkWon={handleMarkWon}
                  onMarkLost={handleMarkLostClick}
                  workflowGuardDecision={guardMarcarGanho}
                />
              ) : undefined}
            />
          </div>
        );
      case 'cadence':
        return (
          <DealCadenceTab
            dealId={deal.id}
            isActive={activeTab === 'cadence'}
            initialStepId={initialStepId ?? null}
            // Contexto real do negócio para popular variáveis do roteiro
            // (contato, valor, fase) e habilitar atalhos WhatsApp/tel/mailto.
            dealContext={{
              contact: deal.contact
                ? {
                    name: deal.contact.name,
                    phone: deal.contact.phone,
                    email: deal.contact.email,
                  }
                : null,
              deal: {
                title: deal.title,
                value: deal.value as number | null,
                currency: deal.currency,
                phase: (deal as any).phase?.name ?? null,
                pipeline: deal.pipeline?.name ?? null,
              },
            }}
          />
        );
      case 'chat':
        return (
          <DealChatTab 
            contactId={deal.contact_id} 
            contactName={deal.contact?.name}
            onOpenConversation={handleOpenConversation}
            isActive={activeTab === 'chat'}
          />
        );
      case 'products':
        return (
          <DealProductsTab 
            dealId={deal.id}
            currency={deal.currency}
            readOnly={deal.status !== 'open'}
            isActive={activeTab === 'products'}
            workspaceId={deal.pipeline?.workspace_id}
          />
        );
      case 'activities':
        return (
          <DealActivitiesTab 
            dealId={deal.id}
            isOpen={deal.status === 'open'}
            isActive={activeTab === 'activities'}
            canCreate={deal.status === 'open'}
          />
        );
      case 'attachments':
        // v1.6.0: aba Anexos — agrega arquivos anexados às atividades da timeline
        return (
          <DealAttachmentsTab
            dealId={deal.id}
            canDelete={deal.status === 'open'}
            isActive={activeTab === 'attachments'}
            onNavigateToActivity={() => setActiveTab('activities')}
            onCountChange={setAttachmentsCount}
          />
        );

      case 'vehicles':
        // v1.7.0: defesa em profundidade — só renderiza com o módulo habilitado
        if (!vehiclesEnabled) return null;
        return (
          <DealVehiclesTab
            dealId={deal.id}
            customerId={deal.contact?.customer?.[0]?.id ?? null}
            contactId={deal.contact_id}
            isActive={activeTab === 'vehicles'}
            readOnly={deal.status !== 'open'}
          />
        );
      case 'proposals':
        // v1.9.0: defesa em profundidade — oculta conteúdo se sem permissão
        if (!canViewProposals) return null;
        return (
          <DealProposalsTab
            dealId={deal.id}
            dealTitle={deal.title}
            isActive={activeTab === 'proposals'}
            canCreate={canCreateProposals}
            canEdit={canEditProposals}
            readOnly={deal.status !== 'open'}
          />
        );
      case 'contracts':
        // v1.6.0: defesa em profundidade — oculta conteúdo se sem permissão de visualização
        if (!canViewContracts) return null;
        return (
          <DealContractsTab
            dealId={deal.id}
            tenantId={deal.tenant_id}
            contactId={deal.contact_id}
            isActive={activeTab === 'contracts'}
            canCreate={canCreateContracts}
            canEdit={canEditContracts}
            canDelete={canDeleteContracts}
          />
        );
      case 'charges':
        // v1.6.0: defesa em profundidade — oculta conteúdo se sem permissão de visualização
        if (!canViewCharges) return null;
        return (
          <DealChargesTab
            dealId={deal.id}
            isActive={activeTab === 'charges'}
            customerId={deal.contact?.customer?.[0]?.id ?? null}
            canCreate={canCreateCharges}
            canEdit={canEditCharges}
            canDelete={canDeleteCharges}
          />
        );
      case 'service_orders':
        // O.S. vinculadas ao negócio: mesma tabela/filtros da listagem oficial de O.S.
        if (!canViewServiceOrders) return null;
        return (
          <div className="flex h-full min-h-0 flex-col">
            {canCreateServiceOrder && deal.contact?.customer?.[0]?.id && (
              <div className="flex justify-end pb-2">
                <Button size="sm" onClick={() => setCreateOsOpen(true)}>Nova O.S.</Button>
              </div>
            )}
            <ServiceOrdersEmbeddedList dealId={deal.id} />
          </div>
        );
      case 'shipments': {
        // Logística: remessas vinculadas ao negócio (componente compartilhado com a O.S.)
        if (!canViewShipments) return null;
        const c = deal.contact?.customer?.[0];
        return (
          <EntityShipmentsTab entityType="deal" entityId={deal.id} entityLabel={deal.title}
            customer={c ? { id: c.id, name: c.nome_fantasia || c.nome || c.razao_social || '' } : null}
            emptyText="Nenhuma remessa vinculada a este negócio." />
        );
      }
      case 'tasks':
        // v1.4.0: lista de tarefas vinculadas ao negócio (reaproveita layout da aba Subtarefas)
        // Defesa em profundidade: oculta conteúdo se módulo de Tarefas estiver desabilitado no tenant
        if (!(currentTenant?.tasks_enabled ?? false)) return null;
        return <TasksRelatedTabs context={{ dealId: deal.id }} />;
      case 'workflow':
        // v2.3.0: aba dedicada aos fluxos de trabalho (antes ficava na aba Resumo).
        // Só é renderizada com o módulo Workflow ligado no tenant.
        if (!workflowEnabled) return null;
        return (
          <WorkflowProcessPanel
            rootEntityType="crm_deal"
            rootEntityId={deal.id}
            groupByStatus
          />
        );
      default:
        return null;
    }
  };

  /**
   * Renderiza conteúdo interno do modal
   */
  const renderContent = () => {
    // Loading state
    if (isLoading) {
      return (
        <div className="flex flex-col h-full">
          {/* Header skeleton */}
          <div className="flex items-center gap-3 px-4 h-12 border-b">
            <Skeleton className="h-5 w-16" />
            <Skeleton className="h-5 w-48 flex-1" />
            <Skeleton className="h-5 w-24" />
            <div className="flex gap-1">
              {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-8 w-8 rounded" />)}
            </div>
          </div>
          {/* Tabs skeleton */}
          <div className="flex items-center gap-2 h-9 px-4 border-b">
            {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-5 w-20" />)}
          </div>
          {/* Content skeleton */}
          <div className="flex flex-1 min-h-0">
            <div className="flex-1 p-4 space-y-4">
              <div className="grid grid-cols-3 gap-3">
                {[1, 2, 3].map(i => <Skeleton key={i} className="h-20 rounded-lg" />)}
              </div>
              <Skeleton className="h-48 rounded-lg" />
            </div>
            <div className="w-[320px] border-l p-3 space-y-4">
              <Skeleton className="h-16 rounded-lg" />
              <Skeleton className="h-12" />
              <Skeleton className="h-32" />
            </div>
          </div>
        </div>
      );
    }

    // Error state
    if (error || !deal) {
      return (
        <div className="flex flex-col items-center justify-center h-full gap-4">
          <p className="text-muted-foreground">Negócio não encontrado.</p>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
        </div>
      );
    }

    // Conteúdo principal
    return (
      <TooltipProvider>
        {/* v2.2.1: overflow-hidden impede o envoltório de ficar mais largo que o modal */}
        <div className="flex flex-col h-full w-full min-w-0 overflow-hidden">
          {/* Header compacto - v2.0.0: canMarkAsWon para validação de produtos */}
          <DealModalHeader
            deal={deal}
            canMarkAsWon={canMarkAsWon}
            canEditDeal={canEditDealHeader}
            canChangePhase={canChangePhaseHeader}
            canReopenDeal={canReopenDealHeader}
            canDeleteDeal={canDeleteDealHeader}
            onEdit={handleEdit}
            onMarkWon={handleMarkWon}
            onMarkLost={handleMarkLostClick}
            onReopen={() => reopenDeal.mutate(deal.id)}
            onDelete={() => setDeleteOpen(true)}
            onClose={() => onOpenChange(false)}
            onStartPlaybook={() => setStartPlaybookOpen(true)}
            onChangePhase={() => setChangePhaseOpen(true)}
            playbookEligibility={playbookEligibility ?? null}
            workflowGuardDecision={guardMarcarGanho}
            onCreateServiceOrder={canCreateServiceOrder && deal.contact?.customer?.[0]?.id ? () => setCreateOsOpen(true) : undefined}
          />
          {canCreateServiceOrder && deal.contact?.customer?.[0] && (
            <ServiceOrderFormDialog open={createOsOpen} onOpenChange={setCreateOsOpen} serviceOrderId={null}
              defaultDealId={deal.id}
              defaultCustomer={{ id: deal.contact.customer[0].id, name: deal.contact.customer[0].nome_fantasia || deal.contact.customer[0].nome || deal.contact.customer[0].razao_social || '' }} />
          )}

          {/* v2.2.0: Trilha de fases clicável (estilo Bitrix) acima das abas.
              2026-10-17: envoltório min-w-0 impede a trilha de esticar o modal
              (a barra de abas passava a medir largura errada e não exibia o "..."). */}
          <div className="w-full min-w-0 overflow-x-auto scrollbar-thin">
          <DealPhaseTrail
            phases={trailPhases}
            currentPhaseId={deal.phase_id}
            canMoveToPhase={canMoveToTrailPhase}
            blockedReason={trailBlockedReason}
            onSelectPhase={handleTrailPhaseSelect}
            readOnly={deal.status !== 'open'}
          />
          </div>



          {/* Sistema de abas - v1.3.0: chargesCount, v1.3.1: financialEnabled, v1.4.0: tasksCount */}
          <DealModalTabs
            activeTab={activeTab}
            onTabChange={setActiveTab}
            chatCount={chatCount}
            productsCount={productsCount}
            contractsCount={contractsCount}
            chargesCount={chargesCount}
            tasksCount={tasksCount}
            attachmentsCount={attachmentsCount}
            vehiclesCount={vehiclesCount}
            proposalsCount={proposalsCount}

            financialEnabled={currentTenant?.financial_enabled ?? false}
            tasksEnabled={currentTenant?.tasks_enabled ?? false}
            vehiclesEnabled={vehiclesEnabled}
            canViewCharges={canViewCharges}
            canViewContracts={canViewContracts}
            canViewProposals={canViewProposals}
            hasCadence={hasCadence}
            cadenceOverdueCount={cadenceOverdueCount}
            workflowEnabled={workflowEnabled}
            workflowCount={workflowProcessos?.length ?? 0}
            shipmentsEnabled={canViewShipments}
            shipmentsCount={shipmentsQuery.data?.total ?? 0}
            serviceOrdersEnabled={canViewServiceOrders}
            serviceOrdersCount={serviceOrdersQuery.data?.kpis?.total ?? 0}
          />

          {/* Área de conteúdo + sidebar */}
          <div className={`flex flex-1 min-h-0 ${isMobile ? 'flex-col' : 'flex-row'}`}>
            {/* Conteúdo principal - cada aba controla seu próprio scroll */}
            <div className="flex-1 min-w-0 min-h-0 overflow-y-auto scrollbar-thin p-2 md:p-4">
              {renderTabContent()}
            </div>

            {/* Sidebar fixa - oculta no mobile (injetada na aba Resumo) */}
            {!isMobile && (
              <DealModalSidebar
                deal={deal}
                canMarkAsWon={canMarkAsWon}
                onViewContact={(contactId) => {
                  // v1.5.0: abre o modal 360º do contato
                  setViewContactId(contactId);
                }}
                onViewCustomer={handleViewCustomer}
                onCreateCustomer={() => {
                  setCustomerFormOpen(true);
                }}
                onInfoSave={handleInfoSave}
                isInfoSaving={updateDeal.isPending || moveDealToPhase.isPending}
                canEditPeople={canEditDealHeader}
                onPeopleSave={handlePeopleSave}
                isPeopleSaving={updateDeal.isPending}
                onMarkWon={handleMarkWon}
                onMarkLost={handleMarkLostClick}
                workflowGuardDecision={guardMarcarGanho}
              />
            )}
          </div>

          {/* Dialog de edição interno (fallback se onEdit não fornecido) */}
          {!onEdit && (
            <DealFormDialog
              open={formOpen}
              onOpenChange={setFormOpen}
              deal={deal}
            />
          )}

          {/* Dialog de confirmação de exclusão */}
          <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Excluir Negócio</AlertDialogTitle>
                <AlertDialogDescription>
                  Tem certeza que deseja excluir "{deal.title}"? Esta ação não pode ser desfeita.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction 
                  onClick={handleDelete}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Excluir
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* v1.5.0: Confirmação para iniciar playbook na fase atual */}
          <AlertDialog open={startPlaybookOpen} onOpenChange={setStartPlaybookOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Iniciar playbook nesta fase</AlertDialogTitle>
                <AlertDialogDescription>
                  {playbookEligibility ? (
                    <>
                      Vamos iniciar o playbook <strong>{playbookEligibility.playbook_name}</strong>
                      {playbookEligibility.phase_name ? (
                        <> na fase <strong>{playbookEligibility.phase_name}</strong></>
                      ) : null}
                      . Serão criadas <strong>{playbookEligibility.steps_in_phase}</strong>{' '}
                      {playbookEligibility.steps_in_phase === 1 ? 'atividade' : 'atividades'} pendentes
                      para este negócio.
                    </>
                  ) : (
                    'Confirme para iniciar o playbook na fase atual.'
                  )}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={startPlaybook.isPending}>Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  disabled={startPlaybook.isPending || !playbookEligibility?.is_eligible}
                  onClick={async (e) => {
                    // Evita fechar antes da resposta para mostrar loading inline
                    e.preventDefault();
                    if (!deal || !playbookEligibility?.playbook_id) return;
                    try {
                      const result = await startPlaybook.mutateAsync({
                        dealId: deal.id,
                        playbookId: playbookEligibility.playbook_id,
                      });
                      toast.success(
                        `Playbook iniciado: ${result.steps_created} ${
                          result.steps_created === 1 ? 'atividade criada' : 'atividades criadas'
                        }.`,
                      );
                      setStartPlaybookOpen(false);
                    } catch (err: any) {
                      logger.error('Falha ao iniciar playbook:', err);
                      toast.error(err?.message ?? 'Erro ao iniciar playbook.');
                    }
                  }}
                >
                  {startPlaybook.isPending ? 'Iniciando...' : 'Iniciar playbook'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>


          {/* Modal de Chat sobreposto ao modal do Deal */}
          <ChatConversationModal
            conversationId={selectedConversationId}
            open={isChatModalOpen}
            onOpenChange={(open) => {
              setIsChatModalOpen(open);
              if (!open) {
                setSelectedConversationId(null);
              }
            }}
          />

          {/* Modal de Cliente sobreposto ao modal do Deal */}
          <CustomerDetailsModalPremium
            customerId={selectedCustomerId}
            open={isCustomerModalOpen}
            onOpenChange={(open) => {
              setIsCustomerModalOpen(open);
              if (!open) {
                setSelectedCustomerId(null);
              }
            }}
          />

          {/* v1.5.0: Modal 360º do contato (modo Contato) aberto pelo card lateral */}
          <CustomerDetailsModalPremium
            customerId={null}
            contactId={viewContactId}
            /* v1.6.0: sem contactPreview o modo Contato abria "vazio" (sem nome/
               telefone/e-mail). Repassamos os dados já carregados do negócio. */
            contactPreview={viewContactId && deal?.contact ? {
              id: deal.contact.id,
              name: deal.contact.name ?? null,
              phone: deal.contact.phone ?? null,
              email: deal.contact.email ?? null,
              avatar_url: deal.contact.avatar_url ?? null,
              created_at: (deal.contact as any).created_at ?? null,
              updated_at: (deal.contact as any).updated_at ?? null,
              seller_id: (deal.contact as any).seller_id ?? null,
              seller_name: (deal.contact as any).seller_name ?? null,
              seller_avatar: (deal.contact as any).seller_avatar ?? null,
            } : null}
            open={!!viewContactId}

            onOpenChange={(open) => {
              if (!open) setViewContactId(null);
            }}
          />

          {/* v1.4.0: Modal de cadastro de cliente (transformar contato em cliente) */}
          <CustomerFormDialog
            customer={null}
            open={customerFormOpen}
            onOpenChange={setCustomerFormOpen}
            prefillContact={deal.contact ? {
              id: deal.contact_id || '',
              name: deal.contact.name,
              phone: deal.contact.phone,
              email: deal.contact.email,
              // Herda vendedor do contato; se indisponível, usa o vendedor do negócio
              seller_id: (deal.contact as any)?.seller_id ?? deal.seller_id ?? null,
            } : null}
            onSuccess={() => {
              setCustomerFormOpen(false);
              // Invalida cache do deal para refletir vínculo contato → cliente
              if (dealId) {
                queryClient.invalidateQueries({ queryKey: ['crm-deal', dealId] });
                queryClient.invalidateQueries({ queryKey: ['deal-modal-data', dealId] });
              }
            }}
          />
          {/* Modal de Ganhar Negócio com Cadastro de Cliente */}
          <WinDealCustomerModal
            dealId={deal.id}
            contactName={deal.contact?.name}
            dealTitle={deal.title}
            open={winDealModalOpen}
            onOpenChange={setWinDealModalOpen}
            onSuccess={() => {
              // Invalida cache do deal para atualizar UI
              setWinDealModalOpen(false);
            }}
          />

          {/* Modal de motivo de perda (obrigatório sempre que marcar como perdido) */}
          <DealLossReasonModal
            open={lossReasonModalOpen}
            onOpenChange={setLossReasonModalOpen}
            dealId={deal?.id ?? null}
            lostPhases={lostPhases.map((p) => ({

              phase_id: p.phase_id,
              phase_name: p.phase_name,
              phase_color: p.phase_color,
            }))}
            onConfirm={handleLossReasonConfirm}
            isPending={markAsLostWithReason.isPending}
          />

          {/* v2.7.0: Modal "Alterar fase" — funciona também com negócio ganho/perdido */}
          <DealChangePhaseDialog
            open={changePhaseOpen}
            onOpenChange={setChangePhaseOpen}
            dealId={deal.id}
            dealStatus={deal.status}
            currentWorkspaceId={deal.pipeline?.workspace_id ?? null}
            currentPipelineId={deal.pipeline_id}
            currentPhaseId={deal.phase_id}
            currentPhaseName={deal.phase?.name ?? null}
            currentPhaseColor={deal.phase?.color ?? null}
            onSuccess={() => {
              // Mesmas invalidações usadas nos demais fluxos de movimentação
              queryClient.invalidateQueries({ queryKey: ['crm-deal', dealId] });
              queryClient.invalidateQueries({ queryKey: ['deal-modal-data', dealId] });
              queryClient.invalidateQueries({ queryKey: ['crm-deals'] });
              queryClient.invalidateQueries({ queryKey: ['kanban-column'] });
              queryClient.invalidateQueries({ queryKey: ['pipeline-phase-stats'] });
              queryClient.invalidateQueries({ queryKey: ['crm-deal-activities', dealId] });
            }}
          />
        </div>
      </TooltipProvider>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className={`p-0 flex flex-col overflow-hidden ${
          isMobile 
            ? 'w-screen h-[100dvh] max-w-none rounded-none border-0' 
            : 'max-w-[95vw] w-[95vw] h-[97vh]'
        }`}
        preventCloseOnOutsideInteraction
        hideCloseButton
      >
        {/* Título acessível oculto */}
        <VisuallyHidden.Root>
          <DialogTitle>Detalhes do Negócio</DialogTitle>
        </VisuallyHidden.Root>
        
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
}
